import { ExcelButton } from "@/controls/buttons/Excel-button";
import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import { ErrorModal, HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	Grid2 as Grid,
	Paper,
	Typography,
	makeStyles,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LazyBackDropLoading } from "../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { ProcesssReportTable } from "./components/ProcessDataReportTable";
import { Filters } from "./components/filters/Filters";
import { useProcesssReportContext } from "./context/ProcesssDataReportContext";
import { getProcesss } from "./repositories/ProcesssDataReportRepository";

const moment = getMomentTz();

export const ProcesssDataReportPage: React.FC = () => {
	const {
		state: {
			processes,
			isLoadingGrid,
			notificationMessage,
			isLoadingBackground,
		},
		actions: { setProcesses, setIsLoadingGrid, setNotificationMessage },
	} = useProcesssReportContext();
	const [error, setError] = useState("");
	const isInitialMount = useRef(true);
	const [isLoading, setIsLoading] = useState(true);
	const gridRef = useRef<AgGridReact>(null);

	const [searchParams, setSearchParams] = useSearchParams();

	const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
		const monthParam = searchParams.get("month");
		if (monthParam) {
			const parsedDate = moment(monthParam);
			return parsedDate.isValid()
				? parsedDate.toDate()
				: moment().startOf("month").toDate();
		}
		return moment().startOf("month").toDate();
	});

	const [selectedAsset, setSelectedAsset] = useState<number | null>(() => {
		const assetParam = searchParams.get("asset");
		return assetParam ? Number(assetParam) : null;
	});

	const handleMonthChange = (month: Date | null) => {
		if (!month) return;
		const firstDayOfMonth = moment(month).startOf("month").toDate();
		setSelectedMonth(firstDayOfMonth);
		updateSearchParams(firstDayOfMonth, selectedAsset);
		getDataFromAPI(firstDayOfMonth, selectedAsset);
	};

	const updateSearchParams = (month: Date, asset: number | null) => {
		const params: { [key: string]: string } = {};
		params.month = month.toISOString();
		if (asset !== null) {
			params.asset = asset.toString();
		}
		setSearchParams(params);
	};

	const handleProcessAssetChange = (assetId: number | null) => {
		setSelectedAsset(assetId);
		updateSearchParams(selectedMonth, assetId);
		getDataFromAPI(selectedMonth, assetId);
	};

	const getDataFromAPI = async (month: Date, asset: number | null) => {
		setIsLoadingGrid(true);
		try {
			const resp = await getProcesss(month, asset);
			if (resp.ok) {
				const data = get(resp, "data.tables[0].rows", []);
				setProcesses(data);
			} else {
				setError(resp.message);
			}
		} catch (err) {
			setError("Error fetching data");
		} finally {
			setIsLoadingGrid(false);
			if (isInitialMount.current) {
				setIsLoading(false);
				isInitialMount.current = false;
			}
		}
	};

	const handleExportToExcel = () => {
		if (gridRef.current?.api) {
			const currentColumns = gridRef.current.api.getColumnDefs();

			if (currentColumns) {
				const params = {
					skipHeader: false,
					skipFooters: true,
					skipGroups: true,
					columnWidth: 100,
					sheetName: "Process Data Report",
					headerRowHeight: 30,
					rowHeight: 25,
					suppressTextAround: true,
					fileName: `swv-ms_heat-report_${moment(selectedMonth).format("YYYY-MM")}${selectedAsset === 11 ? "-EAF1" : selectedAsset === 12 ? "-EAF2" : ""}.xlsx`,
					allColumns: true,
				};

				gridRef.current.api.exportDataAsExcel(params);
				gridRef.current.api.setGridOption("columnDefs", currentColumns);
			}
		}
	};

	useEffect(() => {
		getDataFromAPI(selectedMonth, selectedAsset);
	}, []);

	return (
		<>
			<HelmetDexteel
				title={`Process Data Report - ${moment(selectedMonth).format("MMMM YYYY")}`}
			/>
			<Paper
				elevation={0}
				style={{
					width: "100%",
					padding: "15px 20px 10px",
					height: "93vh",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				<LazyBackDropLoading open={isLoadingBackground} />
				<Grid container justifyContent="center" alignItems="center">
					<Grid size={{ xs: 12 }} container spacing={1}>
						<Grid size={{ xs: 12, md: 12 }}>
							<Typography variant="h5" style={{ fontWeight: 600 }}>
								Process Data Report
							</Typography>
						</Grid>

						<Grid size={{ xs: 12, md: 12 }}>
							<Grid
								container
								direction="row"
								alignItems="stretch"
								justifyContent="space-between"
								spacing={1}
							>
								<Grid size={{ xs: 12, md: "auto" }}>
									<Paper
										elevation={1}
										style={{
											height: "100%",
											display: "flex",
											alignItems: "center",
											backgroundColor: "#fff",
										}}
									>
										<Box sx={{ p: 1 }}>
											<Filters
												onMonthChange={handleMonthChange}
												selectedAsset={selectedAsset}
												onAssetChange={handleProcessAssetChange}
												selectedMonth={selectedMonth}
											/>
										</Box>
									</Paper>
								</Grid>
								<Grid
									size={{ xs: "auto" }}
									style={{
										display: "flex",
										alignItems: "center",
										marginLeft: 16,
									}}
								>
									<div>
										<ExcelButton
											onClick={handleExportToExcel}
											disabled={!processes || processes.length === 0}
										/>
									</div>
								</Grid>
							</Grid>
						</Grid>
						<NotificationSnackBar
							message={notificationMessage}
							onHide={() => setNotificationMessage("")}
						/>
						<Grid size={{ xs: 12 }}>
							{isLoading ? (
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: "400px",
									}}
								>
									<CircularProgress />
								</div>
							) : (
								<ProcesssReportTable
									loadingGrid={isLoadingGrid}
									rows={processes}
									refreshData={() =>
										getDataFromAPI(selectedMonth, selectedAsset)
									}
									gridRef={gridRef}
								/>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Paper>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
